import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search } from 'lucide-react';
import { m, AnimatePresence } from 'framer-motion';
import { useLocale } from '@/lib/i18n';
import { useSectionTranslation } from '@/hooks';
import {
  CURRENCY_LIST,
  POPULAR_CURRENCIES,
  buildSearchIndex,
  searchCurrencies,
  useCurrency,
  type CurrencyRecord,
  type SearchEntry,
} from '@/lib/currency';
import { headerTranslations } from './translations';

const SEARCH_LIMIT = 30;

const CODE_META: Map<string, CurrencyRecord> = new Map(
  CURRENCY_LIST.map((record) => [record.code, record]),
);

function popularRank(code: string): number {
  const i = POPULAR_CURRENCIES.indexOf(code);
  return i === -1 ? POPULAR_CURRENCIES.length : i;
}

function CurrencyFlag({ code }: { code: string }) {
  const meta = CODE_META.get(code.toUpperCase());
  const countryCode = meta?.countryCode;
  if (countryCode) {
    return <span aria-hidden="true" className={`fi fi-${countryCode} rounded-sm`} />;
  }
  return (
    <span
      aria-hidden="true"
      className="flex h-4 min-w-5 items-center justify-center rounded-sm bg-surface-hover px-1 text-[9px] font-bold text-brand-muted"
    >
      {code.slice(0, 3).toUpperCase()}
    </span>
  );
}

export function CurrencySelector({
  className,
  fullWidth = false,
}: {
  className?: string;
  fullWidth?: boolean;
}) {
  const { direction } = useLocale();
  const t = useSectionTranslation(headerTranslations);
  const { currency, setCurrency, recent, status } = useCurrency();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hi, setHi] = useState(-1);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const uid = useId();
  const listboxId = `currency-listbox-${uid.replace(/:/g, '')}`;
  const activeId =
    open && hi >= 0 ? `${listboxId}-option-${hi}` : undefined;

  const isRTL = direction === 'rtl';

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const index = useMemo(() => buildSearchIndex(CURRENCY_LIST), []);

  const fullList = useMemo(
    () =>
      [...index].sort((a, b) => {
        const rank = popularRank(a.code) - popularRank(b.code);
        if (rank !== 0) return rank;
        return a.code < b.code ? -1 : a.code > b.code ? 1 : 0;
      }),
    [index],
  );

  const recentEntries = useMemo(
    () =>
      recent
        .map((code) => CODE_META.get(code.toUpperCase()))
        .filter((entry): entry is CurrencyRecord => entry !== undefined),
    [recent],
  );

  const trimmed = query.trim();
  const showRecent = trimmed === '' && recentEntries.length > 0;
  const mainItems: SearchEntry[] =
    trimmed === '' ? fullList : searchCurrencies(trimmed, index, SEARCH_LIMIT);
  const flatItems: Array<CurrencyRecord | SearchEntry> = showRecent
    ? [...recentEntries, ...mainItems]
    : mainItems;
  const recentCount = showRecent ? recentEntries.length : 0;

  const close = (focusTrigger: boolean) => {
    setOpen(false);
    setQuery('');
    setHi(-1);
    if (focusTrigger) triggerRef.current?.focus();
  };

  const selectEntry = (entry: { code: string }) => {
    // setCurrency persists the preference AND pushes to recent — no manual push.
    setCurrency(entry.code);
    close(true);
  };

  // Autofocus the in-memory search field whenever the panel opens.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open ]);

// Outside-click close (the panel lives in a portal, so check both refs).
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      close(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // The panel is portaled so it escapes the clipping ancestors it renders
  // inside (the profile menu scrolls, the mobile menu animates its height).
  // Anchor it to the trigger on open and keep it glued while the page scrolls.
  useEffect(() => {
    if (!open || isMobile) return;
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPanelPos({ top: rect.bottom + 8, left: rect.left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, isMobile]);

  // Keep the keyboard-highlighted option visible while arrow-navigating.
  useEffect(() => {
    if (!open || hi < 0) return;
    document.getElementById(`${listboxId}-option-${hi}`)?.scrollIntoView({ block: 'nearest' });
  }, [hi, open, listboxId]);

  const onKey = (e: React.KeyboardEvent) => {
    const n = flatItems.length;
    switch (e.key) {
      case 'ArrowDown':
        if (n > 0) {
          e.preventDefault();
          setHi((i) => (i + 1) % n);
        }
        break;
      case 'ArrowUp':
        if (n > 0) {
          e.preventDefault();
          setHi((i) => (i - 1 + n) % n);
        }
        break;
      case 'Enter':
        if (hi >= 0 && hi < n) {
          e.preventDefault();
          selectEntry(flatItems[hi]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        close(true);
        break;
    }
  };

  const renderRow = (entry: CurrencyRecord | SearchEntry, flatIndex: number) => {
    const isCurrent = entry.code.toUpperCase() === currency.toUpperCase();
    const active = hi === flatIndex;
    return (
      <button
        key={`${entry.code}-${flatIndex}`}
        type="button"
        role="option"
        id={`${listboxId}-option-${flatIndex}`}
        aria-selected={isCurrent}
        onClick={() => selectEntry(entry)}
        onMouseEnter={() => setHi(flatIndex)}
        className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-start transition-colors ${
          active ? 'bg-surface-hover text-brand-white' : 'text-brand-muted hover:bg-surface-hover hover:text-brand-white'
        }`}
      >
        <CurrencyFlag code={entry.code} />
        <span className="w-12 shrink-0 font-semibold text-brand-white">{entry.code}</span>
        <span className="flex-1 truncate">{entry.name}</span>
        {entry.symbol && <span className="shrink-0 text-xs text-brand-muted">{entry.symbol}</span>}
        {isCurrent && <Check size={14} className="shrink-0 text-accent" />}
      </button>
    );
  };

  const panelBody = (
    <>
      <div className="p-2 border-b border-surface-border">
        <div className="relative">
          <Search
            size={14}
            className="absolute top-1/2 -translate-y-1/2 start-2.5 text-brand-muted pointer-events-none"
          />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHi(-1);
            }}
            onKeyDown={onKey}
            placeholder={t.currency.searchPlaceholder}
            aria-label={t.currency.searchPlaceholder}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-controls={listboxId}
            aria-activedescendant={activeId}
            autoComplete="off"
            role="combobox"
            className="w-full h-9 rounded-lg bg-surface-base border border-surface-border text-sm text-brand-white placeholder:text-brand-muted focus:outline-none focus:border-accent/30 transition-colors ps-8 pe-3"
          />
        </div>
      </div>
      {status === 'ERROR' && (
        <p className="px-3 py-2 text-[11px] text-brand-muted border-b border-surface-border">
          {t.currency.ratesUnavailable}
        </p>
      )}
      <div
        role="listbox"
        id={listboxId}
        aria-label={t.currency.label}
        className="max-h-72 overflow-y-auto py-1"
      >
        {showRecent && (
          <>
            <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
              {t.currency.recent}
            </p>
            {recentEntries.map((entry, k) => renderRow(entry, k))}
          </>
        )}
        {trimmed === '' && (
          <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
            {t.currency.all}
          </p>
        )}
        {mainItems.map((entry, k) => renderRow(entry, recentCount + k))}
        {mainItems.length === 0 && (
          <p className="px-3 py-6 text-sm text-brand-muted text-center">{t.currency.noResults}</p>
        )}
      </div>
    </>
  );

  return (
    <div ref={rootRef} className={className}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (open) {
            close(false);
          } else {
            setQuery('');
            setHi(-1);
            setOpen(true);
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t.currency.label}
        className={`flex items-center gap-1.5 h-9 px-2 rounded-lg border border-surface-border text-sm text-brand-white hover:border-accent/30 transition-colors ${
          fullWidth ? 'w-full justify-between' : ''
        }`}
      >
        <CurrencyFlag code={currency} />
        <span className={`text-xs font-semibold ${fullWidth ? 'flex-1 text-start ps-1' : 'hidden sm:inline'}`}>
          {currency.toUpperCase()}
        </span>
        <ChevronDown
          size={14}
          className={`text-brand-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {open && !isMobile && panelPos && (
              <m.div
                key="currency-panel"
                ref={panelRef}
                initial={{ opacity: 0, y: 4, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 4, scale: 0.96 }}
                transition={{ duration: 0.15 }}
                className={`fixed z-[60] min-w-[220px] max-w-[calc(100vw-1.5rem)] rounded-xl bg-surface-card border border-surface-border shadow-card overflow-hidden ${
                  isRTL ? 'origin-top-left' : 'origin-top-right'
                }`}
                style={{ top: panelPos.top, left: panelPos.left }}
              >
                {panelBody}
              </m.div>
            )}
            {open && isMobile && (
              <m.div
                key="currency-sheet"
                ref={panelRef}
                initial={{ opacity: 0, y: 16, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 16, scale: 0.98 }}
                transition={{ duration: 0.15 }}
                className="fixed inset-x-3 bottom-3 z-[60] rounded-2xl bg-surface-card border border-surface-border shadow-card overflow-hidden"
              >
                {panelBody}
              </m.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </div>
  );
}
